/**
 * brioCAST Marketing Integrations Page
 * Drop-in for existing CRA portal
 * 
 * Add to App.jsx renderPage():
 *   case "marketing": return hasCap(account, "SCREEN_NETWORK") ? <MarketingPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
 * 
 * Add to Sidebar:
 *   { key: "marketing", label: "行銷綁定", icon: "🔗", cap: "SCREEN_NETWORK" }
 */
import { useState, useEffect } from "react";

const API_BASE_V1 = "https://api.briolabs.io/api/v1";

async function apiV1(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers };
  const res = await fetch(`${API_BASE_V1}${path}`, { ...options, headers, body: options.body ? JSON.stringify(options.body) : undefined });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

const t = {
  bg: "#0F1117", card: "#1A1D27", border: "#2A2E3A", accent: "#6C5CE7",
  accentLight: "#A29BFE", success: "#00B894", danger: "#E17055",
  text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
};

export default function MarketingPage({ account }) {
  const [pixelId, setPixelId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [testCode, setTestCode] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [tokenMask, setTokenMask] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiV1("/tenant/integrations/meta-capi").then((res) => {
      const d = res.data;
      if (d?.connected) {
        setPixelId(d.pixel_id || ""); setTokenMask(d.token_mask || "");
        setTestCode(d.test_code || ""); setConnected(true);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!pixelId || !accessToken) { setError("Pixel ID 與 Access Token 為必填"); return; }
    setSaving(true); setError("");
    try {
      const res = await apiV1("/tenant/integrations/meta-capi", { method: "POST", body: { pixel_id: pixelId, access_token: accessToken, test_event_code: testCode || undefined } });
      setConnected(true); setTokenMask(res.data?.token_mask || ""); setAccessToken("");
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const res = await apiV1("/tenant/integrations/meta-capi/test", { method: "POST" });
      setTestResult({ success: true, message: "測試事件已成功發送至 Meta", event_id: res.data?.event_id });
    } catch (err) { setTestResult({ success: false, message: err.message }); }
  };

  const handleDisconnect = async () => {
    try {
      await apiV1("/tenant/integrations/meta-capi", { method: "DELETE" });
      setPixelId(""); setAccessToken(""); setTestCode(""); setTokenMask("");
      setConnected(false); setTestResult(null);
    } catch (err) { setError(err.message); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: t.textMuted }}>載入中...</div>;

  const inputStyle = { width: "100%", padding: "12px 16px", background: "#141720", border: `1px solid ${t.border}`, borderRadius: 10, color: t.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "'DM Sans', monospace" };
  const labelStyle = { fontSize: 13, color: t.textMuted, marginBottom: 6, display: "block", fontWeight: 500 };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>🔗 數位行銷綁定</h2>
      <p style={{ color: t.textMuted, fontSize: 14, marginBottom: 24 }}>設定 Meta Conversions API 金鑰，啟用精準歸因追蹤</p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, alignItems: "start" }}>
        {/* Main form */}
        <div style={{ background: t.card, borderRadius: 16, border: `1px solid rgba(255,255,255,0.06)`, padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#1877f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Meta Conversions API</div>
              <div style={{ fontSize: 12, color: t.textDim }}>Server-side event tracking · CAPI v18.0</div>
            </div>
            {connected && <span style={{ fontSize: 12, fontWeight: 600, color: t.success, background: `${t.success}18`, padding: "4px 10px", borderRadius: 999 }}>✓ 已連線</span>}
          </div>

          {error && <div style={{ padding: "10px 14px", marginBottom: 16, fontSize: 13, color: t.danger, background: `${t.danger}15`, border: `1px solid ${t.danger}30`, borderRadius: 10 }}>⚠ {error}</div>}

          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Pixel ID</label>
            <input value={pixelId} onChange={(e) => setPixelId(e.target.value)} placeholder="e.g. 1449639233278131" style={inputStyle} />
            <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>Meta Events Manager → 資料來源 → Pixel ID</div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Access Token 🔒</label>
            {connected && !accessToken ? (
              <div style={{ ...inputStyle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: t.textMuted }}>{tokenMask}</span>
                <button onClick={() => setAccessToken(" ")} style={{ background: "none", border: "none", color: t.accentLight, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>變更</button>
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <input type={showToken ? "text" : "password"} value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="EAAGm0PX4ZCps..." style={{ ...inputStyle, paddingRight: 44 }} />
                <button onClick={() => setShowToken(!showToken)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 16 }}>{showToken ? "🙈" : "👁"}</button>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 22 }}>
            <label style={labelStyle}>Test Event Code <span style={{ color: t.textDim, fontWeight: 400 }}>(optional)</span></label>
            <input value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="TEST8829" style={inputStyle} />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={{ padding: "12px 24px", borderRadius: 10, border: "none", background: saving ? t.textDim : t.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving ? "wait" : "pointer" }}>
              {saving ? "⏳ 儲存中…" : "🛡️ 加密儲存"}
            </button>
            {connected && <button onClick={handleTest} style={{ padding: "12px 20px", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 14, cursor: "pointer" }}>⚡ 測試</button>}
            {connected && <button onClick={handleDisconnect} style={{ padding: "12px 16px", borderRadius: 10, border: `1px solid ${t.danger}40`, background: "transparent", color: t.danger, fontSize: 14, cursor: "pointer" }}>斷開</button>}
          </div>

          {testResult && (
            <div style={{ marginTop: 16, padding: "12px 14px", background: testResult.success ? `${t.success}12` : `${t.danger}12`, border: `1px solid ${testResult.success ? t.success : t.danger}25`, borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: testResult.success ? t.success : t.danger }}>{testResult.success ? "✓" : "✕"} {testResult.message}</div>
              {testResult.event_id && <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>event_id: {testResult.event_id}</div>}
            </div>
          )}
        </div>

        {/* Security sidebar */}
        <div style={{ background: t.card, borderRadius: 16, border: `1px solid rgba(255,255,255,0.06)`, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>🛡️ 安全性說明</div>
          <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.8 }}>
            {[
              { icon: "🔒", title: "AES-256-GCM 加密", desc: "Token 經業界最高標準加密存入資料庫" },
              { icon: "🛡️", title: "不落地解密", desc: "僅在發送事件時瞬間解密" },
              { icon: "🔐", title: "租戶隔離", desc: "其他店家無法存取您的金鑰" },
            ].map((item) => (
              <div key={item.title} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <span style={{ flexShrink: 0 }}>{item.icon}</span>
                <div><strong style={{ color: t.text }}>{item.title}</strong><br />{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, padding: "10px 12px", background: "#141720", borderRadius: 8, fontSize: 11, color: t.textDim, fontFamily: "monospace", lineHeight: 1.6 }}>
            Algorithm: AES-256-GCM<br />IV: 12 bytes (random)<br />Auth Tag: 128-bit<br />Storage: iv:tag:cipher
          </div>
        </div>
      </div>
    </div>
  );
}
